import { useEffect, useState } from "react";
import { Trash2, Plus, Pencil, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Header } from '../../components/layout/Header';

const EMPTY = { name: "", email: "", password: "", role: "professional", professional_id: "", can_finance: false };

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "create" | {user}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/professionals"),
      ]);
      setUsers(u.data);
      setPros(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setModal("create"); setShowPwd(false); };
  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: "", role: u.role, professional_id: u.professional_id || "", can_finance: u.can_finance || false });
    setModal(u);
    setShowPwd(false);
  };
  const closeModal = () => setModal(null);

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Nome e e-mail são obrigatórios"); return; }
    if (modal === "create" && !form.password) { toast.error("Defina uma senha"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name,
        email: form.email,
        role: form.role,
        professional_id: form.professional_id || null,
        can_finance: form.can_finance,
        ...(form.password ? { password: form.password } : {}),
      };
      if (modal === "create") {
        await api.post("/admin/users", body);
        toast.success("Usuário criado");
      } else {
        await api.put(`/admin/users/${modal.id}`, body);
        toast.success("Usuário atualizado");
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Excluir ${u.name}?`)) return;
    setDeleting(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Usuário excluído");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleting(null);
    }
  };

  const proName = (pid) => pros.find((p) => p.id === pid)?.name || "—";
  const roleLabel = (r) => ({ admin:"Admin", professional:"Profissional", atendente:"Atendente" }[r] || r);
  const roleBadge = (r) => ({
    admin: "bg-violet-100 text-violet-700 border border-violet-200",
    professional: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    atendente: "bg-sky-50 text-sky-700 border border-sky-200",
  }[r] || "bg-gray-100 text-gray-600 border border-gray-200");

  return (
    <div>
      <Header title="Usuários" subtitle="Gerencie os acessos ao painel administrativo" actions={<button onClick={openCreate} className="admin-btn-primary flex items-center gap-2"><Plus size={16} /> Novo usuário</button>} />

      {loading ? (
        <p className="admin-text-muted">Carregando...</p>
      ) : (
        <div className="admin-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: "var(--admin-border)" }}>
                <th className="text-left px-6 py-3 admin-text-muted font-medium uppercase tracking-wider text-xs">Nome</th>
                <th className="text-left px-6 py-3 admin-text-muted font-medium uppercase tracking-wider text-xs">E-mail</th>
                <th className="text-left px-6 py-3 admin-text-muted font-medium uppercase tracking-wider text-xs">Perfil</th>
                <th className="text-left px-6 py-3 admin-text-muted font-medium uppercase tracking-wider text-xs">Profissional vinculado</th>
                <th className="text-left px-6 py-3 admin-text-muted font-medium uppercase tracking-wider text-xs">Financeiro</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b last:border-0 hover:bg-white/5 transition-colors" style={{ borderColor: "var(--admin-border)" }}>
                  <td className="px-6 py-4 font-medium" style={{ color: "var(--admin-text)" }}>{u.name}</td>
                  <td className="px-6 py-4 admin-text-muted">{u.email}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${roleBadge(u.role)}`}>
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 admin-text-muted">
                    {u.professional_id ? proName(u.professional_id) : <span className="opacity-40">—</span>}
                  </td>
                  <td className="px-6 py-4">
                    {u.can_finance
                      ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">Habilitado</span>
                      : <span className="opacity-30 text-xs">—</span>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(u)} className="admin-btn-ghost p-1.5" title="Editar">
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => del(u)}
                        disabled={deleting === u.id}
                        className="admin-btn-ghost p-1.5 text-red-400 hover:text-red-300"
                        title="Excluir"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="admin-card w-full max-w-md mx-4 p-8 relative">
            <button onClick={closeModal} className="absolute top-4 right-4 admin-btn-ghost p-1"><X size={18} /></button>
            <h2 className="admin-section-title mb-6">{modal === "create" ? "Novo usuário" : "Editar usuário"}</h2>
            <div className="space-y-5">
              <div>
                <label className="admin-label">Nome completo</label>
                <input className="admin-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="admin-label">E-mail</label>
                <input type="email" className="admin-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="admin-label">{modal === "create" ? "Senha" : "Nova senha (deixe em branco para manter)"}</label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    className="admin-input pr-10"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={modal !== "create" ? "••••••••" : ""}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 admin-text-muted hover:text-white"
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="admin-label">Perfil de acesso</label>
                <select className="admin-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="professional">Profissional</option>
                  <option value="atendente">Atendente (fluxo + atendimento)</option>
                  <option value="admin">Admin (acesso total)</option>
                </select>
              </div>
              {(form.role === "atendente" || form.role === "professional") && (
                <div className="flex items-center justify-between p-3 rounded-lg border" style={{borderColor:"var(--admin-border)"}}>
                  <div>
                    <p className="text-sm font-medium" style={{color:"var(--admin-text)"}}>Acesso financeiro</p>
                    <p className="text-xs admin-text-muted mt-0.5">Permite ver e registrar lançamentos financeiros</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, can_finance: !f.can_finance }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.can_finance ? "bg-amber-500" : "bg-gray-300"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.can_finance ? "translate-x-6" : "translate-x-1"}`}/>
                  </button>
                </div>
              )}
              {form.role === "professional" && (
                <div>
                  <label className="admin-label">Vincular ao profissional</label>
                  <select className="admin-input" value={form.professional_id} onChange={(e) => setForm({ ...form, professional_id: e.target.value })}>
                    <option value="">— Nenhum —</option>
                    {pros.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <p className="text-xs admin-text-muted mt-1">O profissional verá apenas os próprios agendamentos.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={closeModal} className="admin-btn-ghost">Cancelar</button>
              <button onClick={save} disabled={saving} className="admin-btn-primary">
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
